// Port of RelativePowerCalc math from xert-wasm/relativePower.cpp.
// All constants match the C++ defaults (75 kg reference rider, 400 W reference power).

const BIKE_WEIGHT = 9;   // kg
const G = 9.81;           // m/s²
const RHO = 1.224;        // kg/m³
const M_REF = 75;         // kg — reference rider body mass
const P_REF = 400;        // W  — reference power used to find reference speed
const C_RR = 0.0033;      // rolling resistance

// CdA interpolation: two rider mass reference points
const CD_a = 0.26, M_A = 60;
const CD_b = 0.30, M_B = 90;
const CD_0 = 0.11;

const CD0 = 0.5 * RHO * CD_0;
const CDA = 0.5 * RHO * CD_a - CD0;
const CDB = 0.5 * RHO * CD_b - CD0;
const K_M = Math.log(CDB / CDA) / Math.log(M_B / M_A);
const K_B = CDA / Math.pow(M_A, K_M);

// Total reference mass (rider + bike)
const M_TOT = M_REF + BIKE_WEIGHT; // 84 kg

// Solves A·v³ + C·v + D = 0 (B = 0) for the positive real root.
// Matches speedAtPower() in relativePower.cpp.
function speedAtPower(totalMassKg: number, power: number, sinGrade: number): number {
    const C_D = CD0 + K_B * Math.pow(totalMassKg, K_M);
    const A = C_D;
    const C = totalMassKg * G * (sinGrade + C_RR);
    const D = -power;

    const P = C / A;
    const Q = D / A;

    const discriminant = Q * Q / 4 + P * P * P / 27;
    if (discriminant < 0) {
        // Trigonometric solution — 3 real roots; k=0 yields the positive one.
        return 2 * Math.sqrt(-P / 3) * Math.cos(Math.acos(3 * Q * Math.sqrt(-3.0 / P) / (2 * P)) / 3);
    }
    const R = Math.sqrt(discriminant);
    return Math.cbrt(-Q / 2 + R) + Math.cbrt(-Q / 2 - R);
}

// Returns the grade-dependent power scaling exponent (Kge).
// Matches getFactor(weight, v, sinGrade) in relativePower.cpp.
function computeKge(totalMassKg: number, v: number, sinGrade: number): number {
    const xkm = Math.pow(totalMassKg, K_M);
    const v2 = v * v;
    const Gx = G * (sinGrade + C_RR) * totalMassKg;
    return (K_B * K_M * v2 * xkm + Gx) / ((CD0 + K_B * xkm) * v2 + Gx);
}

/** Converts a grade percentage (e.g. 5 for 5%) to sin(angle). */
export function gradePctToSinGrade(pct: number): number {
    const g = pct / 100;
    return g / Math.sqrt(1 + g * g);
}

/**
 * Returns the grade-dependent power scaling exponent (Kge) for the reference
 * rider, evaluated at the reference power. This is the exponent applied to the
 * mass ratio in getRelativePowerFactor.
 */
export function getKgeForGrade(sinGrade: number): number {
    const v = speedAtPower(M_TOT, P_REF, sinGrade);
    return Math.max(-1.0, computeKge(M_TOT, v, sinGrade));
}

/**
 * Returns the multiplicative factor to convert a rider's absolute power to
 * the equivalent power a 75 kg reference rider would need at the same speed
 * on the given grade.
 *
 * Matches adjustPower(m, power, sin_grade) / power in relativePower.cpp.
 */
export function getRelativePowerFactor(userWeightKg: number, sinGrade: number): number {
    const kge = getKgeForGrade(sinGrade);
    const userTotal = userWeightKg + BIKE_WEIGHT;
    return Math.pow(M_TOT / userTotal, kge);
}

/**
 * Scales a raw power value to relative power for a rider of the given weight
 * at the given grade percentage.
 */
export function scaleToRelativePower(power: number, userWeightKg: number, sinGrade: number): number {
    return power * getRelativePowerFactor(userWeightKg, sinGrade);
}
